import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Download,
  Database,
  TableProperties,
  BarChart3,
  Play,
  RotateCcw,
  Search,
  Plus,
  AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useDatasetExplorerStore } from "./datasetExplorerStore";
import DatasetPicker from "./components/DatasetPicker";
import JoinPicker from "./components/JoinPicker";
import FieldPicker from "./components/FieldPicker";
import FilterBuilder from "./components/FilterBuilder";
import GroupByOrderBy from "./components/GroupByOrderBy";
import ResultsTable from "./components/ResultsTable";
import ResultsChart from "./components/ResultsChart";
import DatasetChatView from "./components/DatasetChatView";
import { exportToCsv } from "./utils/csvExport";

/**
 * Main controller for the Dataset Explorer.
 * Chat mode: full-width chat view for natural-language queries.
 * Explorer mode: two-panel layout with query builder sidebar and results.
 */
export default function DatasetExplorerController() {
  const fetchSchemas = useDatasetExplorerStore((s) => s.fetchSchemas);
  const schemasLoading = useDatasetExplorerStore((s) => s.schemasLoading);
  const mode = useDatasetExplorerStore((s) => s.mode);
  const selectedDataset = useDatasetExplorerStore((s) => s.selectedDataset);
  const selectedFields = useDatasetExplorerStore((s) => s.selectedFields);
  const executeQuery = useDatasetExplorerStore((s) => s.executeQuery);
  const resultLoading = useDatasetExplorerStore((s) => s.resultLoading);
  const result = useDatasetExplorerStore((s) => s.result);
  const viewMode = useDatasetExplorerStore((s) => s.viewMode);
  const setViewMode = useDatasetExplorerStore((s) => s.setViewMode);
  const resultError = useDatasetExplorerStore((s) => s.resultError);
  const resetQuery = useDatasetExplorerStore((s) => s.resetQuery);
  const hydrateFromUrl = useDatasetExplorerStore((s) => s.hydrateFromUrl);

  useEffect(() => {
    fetchSchemas().then(() => {
      hydrateFromUrl();
    });
  }, [fetchSchemas, hydrateFromUrl]);

  if (schemasLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (mode === "chat") {
    return (
      <div className="h-[calc(100vh-8rem)] rounded-lg border bg-background overflow-hidden">
        <DatasetChatView />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] rounded-lg border bg-background overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Query sidebar */}
        <ScrollArea className="w-80 h-full shrink-0 border-r bg-muted/30">
          <div className="p-4 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold tracking-tight">
                  Query Builder
                </h2>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={resetQuery}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                New Query
              </Button>
            </div>

            <DatasetPicker />

            {selectedDataset && (
              <div className="contents animate-stagger">
                <Separator />
                <JoinPicker />
                <FieldPicker />
                <Separator />
                <FilterBuilder />
                <Separator />
                <GroupByOrderBy />
                <Separator />

                <div className="flex gap-2 pt-1">
                  <Button
                    className="flex-1"
                    onClick={executeQuery}
                    disabled={selectedFields.length === 0 || resultLoading}
                  >
                    {resultLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-3.5 w-3.5 mr-2" />
                    )}
                    Run Query
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={resetQuery}
                    className="group"
                  >
                    <RotateCcw className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-rotate-90" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Results panel */}
        <div className="flex-1 flex flex-col min-w-0">
          {resultError && !resultLoading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-8">
              <Alert variant="destructive" className="max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Query failed</AlertTitle>
                <AlertDescription>
                  This query could not be executed. Try adjusting your selected
                  fields, filters, or aggregations and run again.
                </AlertDescription>
              </Alert>
            </div>
          ) : !result && !resultLoading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
              <Search className="h-10 w-10 opacity-30 animate-gentle-float" />
              <div className="text-center">
                <p className="text-sm font-medium">Ready to query</p>
                <p className="text-xs mt-1">
                  Select fields and click Run Query to see results.
                </p>
              </div>
            </div>
          ) : (
            <Tabs
              value={viewMode}
              onValueChange={(v) => setViewMode(v as "table" | "chart")}
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/20">
                <div className="flex items-center gap-3">
                  <TabsList className="h-8">
                    <TabsTrigger value="table" className="text-xs px-3">
                      <TableProperties className="h-3.5 w-3.5 mr-1.5" />
                      Table
                    </TabsTrigger>
                    <TabsTrigger value="chart" className="text-xs px-3">
                      <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                      Chart
                    </TabsTrigger>
                  </TabsList>

                  {result && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {result.rowCount} row{result.rowCount !== 1 ? "s" : ""}
                      {result.truncated ? " (truncated)" : ""}
                    </span>
                  )}
                </div>

                {result && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs group"
                    onClick={() => exportToCsv(result)}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5 transition-transform duration-200 group-hover:translate-y-0.5" />
                    Export CSV
                  </Button>
                )}
              </div>

              <div className="flex-1 overflow-auto p-4 animate-fade-in-up">
                <TabsContent value="table" className="mt-0">
                  <ResultsTable />
                </TabsContent>
                <TabsContent value="chart" className="mt-0">
                  <ResultsChart />
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}
